'use client';

import {useDevConfig} from 'common/dev';
import {FRONTEND_URL} from 'config';
import {theLoopReturnUrl} from 'config';
import {useSubmissionFlow} from 'iq-interview_experiences/hooks';
import type {SubmissionFlowConfig} from 'iq-interview_experiences/types';
import {SubmissionSource} from 'iq-interview_experiences/types';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useEffect} from 'react';
import {FlowDiaformCta} from './flow_diaform_cta';
import {FlowProgress} from './flow_progress';
import {FlowStepDetails} from './flow_step_details';
import {FlowStepExperience} from './flow_step_experience';
import {FlowStepQuestionsAndTakehomeResolver} from './flow_step_questions_and_takehome_resolver';
import {FlowSuccess} from './flow_success';

import {AuthForm} from 'iq-auth/components/auth_form';
import {Loader2} from 'lucide-react';

type SubmissionFlowProps = {
  config?: SubmissionFlowConfig;
  guideSlug?: string;
  defaultCompanySlug?: string;
  defaultPositionSlug?: string;
  /** Render without full-page height — for use inside a modal. */
  compact?: boolean;
  /** Called when the submission completes successfully. */
  onSuccess?: () => void;
  /** Called when the success CTA is pressed in a modal — closes (and reloads) instead of navigating. */
  onDismiss?: () => void;
};

function parseSubmissionSource(raw: string | null): SubmissionSource | undefined {
  if (raw === SubmissionSource.TheLoop) return SubmissionSource.TheLoop;
  if (raw === SubmissionSource.UserDashboard) return SubmissionSource.UserDashboard;
  return undefined;
}

function diaformSourceFromSource(source: string | null, guideSlug?: string): string {
  if (source === SubmissionSource.TheLoop) return SubmissionSource.TheLoop;
  if (source === SubmissionSource.UserDashboard) return SubmissionSource.UserDashboard;
  if (guideSlug) return 'guide';
  return 'standalone';
}

function backHrefFromSource(source: string | null): string | null {
  if (source === SubmissionSource.TheLoop) return theLoopReturnUrl();
  if (source === SubmissionSource.UserDashboard) return `${FRONTEND_URL}/dashboard/my-interviews`;
  return null;
}

function FlowBackButton({step, onBack}: {step: number; onBack: () => void}) {
  const source = useSearchParams().get('source');
  if (step === 0) {
    const href = backHrefFromSource(source);
    if (!href) return null;
    return (
      <Link
        href={href}
        className='text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
        ← Back
      </Link>
    );
  }
  return (
    <button
      type='button'
      onClick={onBack}
      className='text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
      ← Back
    </button>
  );
}

export function SubmissionFlow({
  config,
  guideSlug,
  defaultCompanySlug,
  defaultPositionSlug,
  compact,
  onSuccess,
  onDismiss,
}: SubmissionFlowProps = {}) {
  const {prefillEmail} = useDevConfig();
  const source = useSearchParams().get('source');
  const diaformSource = diaformSourceFromSource(source, guideSlug);
  const {
    step,
    direction,
    transitioning,
    formData,
    setField,
    validating,
    submitting,
    validationError,
    submitted,
    canProceed,
    goToStep,
    handleNext,
    handleAuthSuccess,
    addTakehomeFiles,
    removeTakehomeFile,
    resolvedConfig,
    totalSteps,
    submissionId,
  } = useSubmissionFlow(config, {
    guideSlug,
    defaultCompanySlug,
    defaultPositionSlug,
    source: parseSubmissionSource(source),
  });

  useEffect(() => {
    if (submitted) onSuccess?.();
  }, [submitted, onSuccess]);

  if (submitted) {
    return compact ? (
      <div className='p-8'>
        <FlowSuccess guideSlug={guideSlug} onDismiss={onDismiss} />
      </div>
    ) : (
      <div className='min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12'>
        <div className='w-full max-w-3xl'>
          <FlowSuccess guideSlug={guideSlug} onDismiss={onDismiss} />
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className='overflow-y-auto'>
        <div className='p-8 flex flex-col gap-6'>
          {step < totalSteps && <FlowProgress step={step} totalSteps={totalSteps} />}
          <div
            className='transition duration-200 ease-in-out'
            style={{
              opacity: transitioning ? 0 : 1,
              transform: transitioning ? `translateX(${direction * 24}px)` : 'translateX(0)',
            }}>
            {step === 0 && resolvedConfig.allowDiaform && (
              <FlowDiaformCta
                submissionId={submissionId}
                source={diaformSource}
                guideSlug={guideSlug}
              />
            )}
            {step === 0 && (
              <FlowStepDetails
                step={step}
                totalSteps={totalSteps}
                data={formData}
                onField={setField}
                validationError={validationError}
              />
            )}
            {step === 1 && (
              <FlowStepExperience
                step={step}
                totalSteps={totalSteps}
                value={formData.experience}
                onChange={(v) => setField('experience', v)}
                validationError={validationError}
                validating={validating}
              />
            )}
            {step === 2 && (
              <FlowStepQuestionsAndTakehomeResolver
                step={step}
                totalSteps={totalSteps}
                config={resolvedConfig}
                questions={formData.questions}
                onQuestionsChange={(v: string) => setField('questions', v)}
                validationError={validationError}
                takeHomeFiles={formData.takeHomeFiles}
                onAddTakehomeFiles={addTakehomeFiles}
                onRemoveTakehomeFile={removeTakehomeFile}
              />
            )}
            {step === 3 && (
              <AuthForm
                onBack={() => goToStep(2, -1)}
                onSuccess={handleAuthSuccess}
                cta='Sign up or log in to submit your interview experience.'
                initialEmail={prefillEmail ?? undefined}
                next={backHrefFromSource(source) ?? undefined}
              />
            )}
          </div>

          {step < totalSteps && (
            <div className='flex items-center justify-between'>
              <div>
                {step > 0 && (
                  <button
                    type='button'
                    onClick={() => goToStep(step - 1, -1)}
                    className='text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
                    ← Back
                  </button>
                )}
              </div>
              <button
                type='button'
                onClick={handleNext}
                disabled={!canProceed || validating || submitting}
                className='flex items-center gap-2 px-6 py-3 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40'>
                {(validating || submitting) && <Loader2 className='w-4 h-4 animate-spin' />}
                {validating
                  ? 'Validating...'
                  : submitting
                    ? 'Submitting...'
                    : step === 2
                      ? 'Submit'
                      : 'Continue'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 pt-8 pb-24 bg-iq-grey-lighter'>
      <div className='w-full max-w-3xl'>
        {step < totalSteps && <FlowProgress step={step} totalSteps={totalSteps} />}

        <div
          className='transition duration-200 ease-in-out'
          style={{
            opacity: transitioning ? 0 : 1,
            transform: transitioning ? `translateX(${direction * 24}px)` : 'translateX(0)',
          }}>
          {step === 0 && resolvedConfig.allowDiaform && (
            <FlowDiaformCta
              submissionId={submissionId}
              source={diaformSource}
              guideSlug={guideSlug}
            />
          )}
          {step === 0 && (
            <FlowStepDetails
              step={step}
              totalSteps={totalSteps}
              data={formData}
              onField={setField}
              validationError={validationError}
            />
          )}
          {step === 1 && (
            <FlowStepExperience
              step={step}
              totalSteps={totalSteps}
              value={formData.experience}
              onChange={(v) => setField('experience', v)}
              validationError={validationError}
              validating={validating}
            />
          )}
          {step === 2 && (
            <FlowStepQuestionsAndTakehomeResolver
              step={step}
              totalSteps={totalSteps}
              config={resolvedConfig}
              questions={formData.questions}
              onQuestionsChange={(v: string) => setField('questions', v)}
              validationError={validationError}
              takeHomeFiles={formData.takeHomeFiles}
              onAddTakehomeFiles={addTakehomeFiles}
              onRemoveTakehomeFile={removeTakehomeFile}
            />
          )}
          {step === 3 && (
            <AuthForm
              onBack={() => goToStep(2, -1)}
              onSuccess={handleAuthSuccess}
              initialEmail={prefillEmail ?? undefined}
            />
          )}
        </div>

        {step < totalSteps && (
          <div className='flex items-center justify-between mt-6'>
            <div>
              <FlowBackButton step={step} onBack={() => goToStep(step - 1, -1)} />
            </div>
            <button
              type='button'
              onClick={handleNext}
              disabled={!canProceed || validating || submitting}
              className='flex items-center gap-2 px-6 py-3 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40'>
              {(validating || submitting) && <Loader2 className='w-4 h-4 animate-spin' />}
              {validating
                ? 'Validating...'
                : submitting
                  ? 'Submitting...'
                  : step === 2
                    ? 'Submit'
                    : 'Continue'}
            </button>
          </div>
        )}

        {step > 0 && step < totalSteps && (
          <p className='text-xs text-iq-grey-medium mt-3 text-right'>
            Press Ctrl+Enter to continue
          </p>
        )}
      </div>
    </div>
  );
}
